/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { invalidate } from '@react-three/fiber';
import { t } from 'i18next';
import { Ray, Vector3 } from 'three';
import { v4 as uuidv4 } from 'uuid';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';

import { SettingModelSettingType, type ArticleGroupPlacement, type PartPlacement } from 'api/models';
import { HubConnectionEvent } from 'app/modules/wallforming/components/content-container/types/hubConnectionEvent';
import { PlacementUpdateEvent } from 'app/modules/wallforming/components/content-container/types/placementUpdateEvent';
import { ArticleEdgeGeometries } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleEdgeGeometries';
import { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import { Part } from 'app/modules/wallforming/components/scenes/objects/articles/types/part';
import {
    cloneArticleInstance,
    clonePart,
    isAdjustableFramiClamp,
    isFramiWedgeClamp,
    isSupportingConstruction,
    mapArticleInstanceToPlacement,
    mapArticleLifeCycleToIndex,
    mapPartsArrayToRecord,
    mapPlacementToArticleInstance
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { getNearestSnapCandidate } from 'app/modules/wallforming/components/scenes/objects/articles/utils/clipboardUtils';
import {
    findNearestConnectorSnapTarget,
    getAnchorPart,
    getConnectorMainPartName,
    isTopBottomCameraView,
    raycastPlane,
    placeInstanceOnWall
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { removeCachedInstanceEdges } from 'app/modules/wallforming/components/scenes/objects/articles/utils/edgesOverlayUtils';
import { TransformationMode } from 'app/modules/wallforming/components/scenes/objects/helpers/types/transformationMode';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import {
    getRelatedPoiIndices,
    isCornerSingleSidePoi,
    isPlanarFacePoi
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import {
    finishBatchProcessing,
    startBatchProcessing
} from 'app/modules/wallforming/components/state-management/placement/slices/connectionSlice';
import { postArticleBatchTask } from 'app/modules/wallforming/components/workers';
import { EMPTY_GUID } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { ArticleSliceState } from 'app/modules/wallforming/components/state-management/placement/states/articleSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

const initialParts = new Map<string, Part>();
const initialPosition = new Vector3();

let activeBatch: {
    promise: Promise<void>;
    placements: ArticleGroupPlacement[];
    sessionId: string;
} | null = null;
let lastPlacementMutation: Promise<void> = Promise.resolve();

/**
 * Wait for successful SignalR connection/reconnection before sending batch updates.
 */
const waitForHubConnection = async (
    get: () => PlacementState,
    expectedSessionId: string
): Promise<{ connection: HubConnection; sessionId: string }> => {
    for (;;) {
        const { hubConnection, sessionId, isHubConnected } = get();
        if (sessionId !== expectedSessionId) {
            throw new Error(t('placementSessionChanged'));
        }
        if (hubConnection?.state === HubConnectionState.Connected && isHubConnected && sessionId) {
            return { connection: hubConnection, sessionId };
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
    }
};

export function scheduleBatchPlace(newPlacements: ArticleGroupPlacement[], get: () => PlacementState) {
    if (newPlacements.length === 0) {
        return Promise.resolve();
    }
    const queuedSessionId = get().sessionId;
    if (activeBatch && activeBatch.sessionId === queuedSessionId) {
        activeBatch.placements.push(...newPlacements);
        return activeBatch.promise;
    }

    const previous = lastPlacementMutation;
    const batch = { promise: Promise.resolve(), placements: [...newPlacements], sessionId: queuedSessionId };
    const batchPromise = (async () => {
        try {
            await previous;
            /* Continue processing until no new placements were added:
               New placements may be added while a previous batch is still being prepared.
            */
            while (batch.placements.length > 0) {
                const placements = batch.placements.splice(0);
                const { hubChunks } = await postArticleBatchTask<{
                    hubChunks: Array<
                        Array<Omit<ArticleGroupPlacement, 'lifeCycleCode'> & { lifeCycleCode: number | null }>
                    >;
                }>({
                    type: 'prepareBatchPlace',
                    placements: placements.map((group) => ({
                        ...group,
                        lifeCycleCode: mapArticleLifeCycleToIndex(group.lifeCycleCode)
                    }))
                });
                const { connection, sessionId } = await waitForHubConnection(get, queuedSessionId);

                for (const chunk of hubChunks) {
                    await connection.send(HubConnectionEvent.PLACE_ARTICLE_GROUPS, sessionId, chunk);
                    await new Promise((resolve) => setTimeout(resolve, 2));
                }
            }
        } catch (e) {
            console.error('Placement error: ', e);
            if (get().sessionId === queuedSessionId) {
                get().clearHistory();
            }
            toastNotifications.error({ title: t('error'), message: t('placementSyncFailed') });
        } finally {
            // Prevent cleanup of active batches via previous batches
            if (activeBatch === batch) {
                activeBatch = null;
            }
        }
    })();

    batch.promise = batchPromise;
    activeBatch = batch;
    lastPlacementMutation = batchPromise;
    return batchPromise;
}

export function scheduleBatchDelete(instanceIds: string[], get: () => PlacementState): void {
    if (instanceIds.length === 0) {
        return;
    }
    const previous = lastPlacementMutation;
    const queuedSessionId = get().sessionId;
    // A delete is an ordering barrier; later placements must not join an earlier pending batch.
    activeBatch = null;
    lastPlacementMutation = (async () => {
        try {
            await previous;
            const { connection, sessionId } = await waitForHubConnection(get, queuedSessionId);
            await connection.send(HubConnectionEvent.DELETE_ARTICLES, sessionId, instanceIds);
        } catch (error) {
            console.error('Placement deletion error: ', error);
            if (get().sessionId === queuedSessionId) {
                get().clearHistory();
            }
            toastNotifications.error({ title: t('error'), message: t('placementSyncFailed') });
        }
    })();
}

export const createArticleSlice: StateCreator<PlacementState, [], [], ArticleSliceState> = (set, get) => ({
    articles: {},
    setArticles: (articles) => set({ articles }),
    setArticleElementType: (articleId, elementType) =>
        set((state) => {
            const article = state.articles[articleId];
            if (!article || article.elementType === elementType) {
                return state;
            }

            return {
                articles: {
                    ...state.articles,
                    [articleId]: { ...article, elementType }
                }
            };
        }),
    batchAddArticles: (entries) =>
        set((state) => {
            const updatedArticles = { ...state.articles };
            let hasChanges = false;

            for (const entry of entries) {
                if (!entry.id || updatedArticles[entry.id]) {
                    continue;
                }

                updatedArticles[entry.id] = {
                    articleNumber: entry.articleNumber,
                    articleInstances: {},
                    edgeGeometries: null,
                    lifeCycleCode: entry.lifeCycleCode,
                    meshesCount: 0,
                    meshInfos: [],
                    partNames: []
                };
                hasChanges = true;
            }

            return hasChanges ? { articles: updatedArticles } : state;
        }),

    addPlacementIndicator: (placements) => {
        const selectedPoiIndex = get().poiSelection.current.index;
        const selectedPoiGuid = selectedPoiIndex !== undefined ? get().pois[selectedPoiIndex]?.poi.poiGuid : undefined;

        // Ensure that new placements have stable IDs and POI references
        const normalizedPlacements = placements.map((articleGroup) => ({
            ...articleGroup,
            instances: articleGroup.instances.map((instance) => ({
                ...instance,
                id: instance.id || uuidv4(),
                poiGuids: instance.poiGuids?.length ? instance.poiGuids : selectedPoiGuid ? [selectedPoiGuid] : []
            }))
        }));

        startBatchProcessing();

        set((state) => {
            const updatedArticles = { ...state.articles };
            const clonedInstanceMaps = new Set<string>();
            const localPoiArticleUpdates = new Map<number, Map<string, string[]>>();
            let poiGuidToIndex: Map<string, number> | null = null;

            const getPoiIndex = (poiGuid: string): number | undefined => {
                if (!poiGuid || poiGuid === EMPTY_GUID) {
                    return undefined;
                }

                if (poiGuidToIndex === null) {
                    poiGuidToIndex = new Map<string, number>();
                    state.pois.forEach((poiData, index) => {
                        poiGuidToIndex!.set(poiData.poi.poiGuid, index);
                    });
                }

                return poiGuidToIndex.get(poiGuid);
            };

            for (const articleGroup of normalizedPlacements) {
                const articleGuid = articleGroup.articleGuid;

                if (!updatedArticles[articleGuid]) {
                    updatedArticles[articleGuid] = {
                        articleNumber: articleGroup.articleNumber,
                        articleInstances: {},
                        edgeGeometries: null,
                        lifeCycleCode: articleGroup.lifeCycleCode,
                        meshesCount: 0,
                        meshInfos: [],
                        partNames: []
                    };
                    clonedInstanceMaps.add(articleGuid);
                }

                if (!clonedInstanceMaps.has(articleGuid)) {
                    updatedArticles[articleGuid] = {
                        ...updatedArticles[articleGuid],
                        articleInstances: { ...updatedArticles[articleGuid].articleInstances }
                    };
                    clonedInstanceMaps.add(articleGuid);
                }

                const instanceMap = updatedArticles[articleGuid].articleInstances;

                for (const instance of articleGroup.instances) {
                    const articleInstanceId = instance.id || uuidv4();
                    const mappedInstance = mapPlacementToArticleInstance(instance);

                    const resolvedPoiIndices = new Set<number>();
                    for (const poiGuid of instance.poiGuids ?? []) {
                        const poiIndex = getPoiIndex(poiGuid);
                        if (poiIndex !== undefined) {
                            getRelatedPoiIndices(state.pois, poiIndex).forEach((relatedPoiIndex) =>
                                resolvedPoiIndices.add(relatedPoiIndex)
                            );
                        }
                    }
                    if (resolvedPoiIndices.size === 0 && state.poiSelection.current.index !== undefined) {
                        getRelatedPoiIndices(state.pois, state.poiSelection.current.index).forEach((poiIndex) =>
                            resolvedPoiIndices.add(poiIndex)
                        );
                        mappedInstance.poiGuids = [...resolvedPoiIndices]
                            .map((poiIndex) => state.pois[poiIndex]?.poi.poiGuid)
                            .filter((poiGuid): poiGuid is string => Boolean(poiGuid));
                    }
                    instanceMap[articleInstanceId] = mappedInstance;
                    resolvedPoiIndices.forEach((relatedPoiIndex) => {
                        const poiArticleMap =
                            localPoiArticleUpdates.get(relatedPoiIndex) ?? new Map<string, string[]>();
                        const articleIds = poiArticleMap.get(articleGuid) ?? [];
                        articleIds.push(articleInstanceId);
                        poiArticleMap.set(articleGuid, articleIds);
                        localPoiArticleUpdates.set(relatedPoiIndex, poiArticleMap);
                    });
                }
            }

            const updatedPois = [...state.pois];
            localPoiArticleUpdates.forEach((articleMap, resolvedPoiIndex) => {
                const existingPoi = updatedPois[resolvedPoiIndex];
                if (!existingPoi) {
                    return;
                }

                const updatedPoiArticles = { ...existingPoi.articles };
                articleMap.forEach((instanceIds, articleGuid) => {
                    const existingInstances = updatedPoiArticles[articleGuid] ?? [];
                    updatedPoiArticles[articleGuid] = [...existingInstances, ...instanceIds];
                });

                updatedPois[resolvedPoiIndex] = { ...existingPoi, articles: updatedPoiArticles };
            });

            return { articles: updatedArticles, pois: updatedPois };
        });

        scheduleBatchPlace(normalizedPlacements, get);
        finishBatchProcessing();
        invalidate();
    },

    removeArticle: (articleId) =>
        set((state) => {
            const { [articleId]: _, ...rest } = state.articles;

            const toRemoveIds = Object.keys(state.articles[articleId]?.articleInstances ?? {});
            for (const instanceId of toRemoveIds) {
                state.removeCollidersByRelation(instanceId);
                initialParts.delete(instanceId);
            }
            return { articles: rest };
        }),

    syncArticleEdgeGeometries: (articleId, edgeGeometries: ArticleEdgeGeometries, meshesCount, meshInfos) =>
        set((state) => {
            const article = state.articles[articleId];
            if (!article) {
                return state;
            }

            if (
                article.meshesCount === meshesCount &&
                article.meshInfos === meshInfos &&
                article.edgeGeometries === edgeGeometries
            ) {
                return state;
            }

            const updated = { ...article };
            if (updated) {
                updated.meshesCount = meshesCount;
                updated.meshInfos = meshInfos;
                updated.edgeGeometries = edgeGeometries;
            }
            return { articles: { ...state.articles, [articleId]: updated } };
        }),

    addOrUpdateArticleInstance: (articleId, articleInstanceId, articleInstance, propagateUpdate, isNewPlacement) => {
        const currentState = get();
        const keepManualPlacementData =
            isNewPlacement &&
            (currentState.isDragNewArticleActiveRef.current || currentState.isDragNewArticleStartRef.current) &&
            currentState.manualPlacementData.articleId === articleId &&
            currentState.manualPlacementData.candidates.length > 0 &&
            (currentState.manualPlacementData.settingType === SettingModelSettingType.Connectors ||
                currentState.manualPlacementData.settingType === SettingModelSettingType.Corners ||
                currentState.manualPlacementData.settingType === SettingModelSettingType.Anchors);

        if (!keepManualPlacementData) {
            currentState.clearManualPlacementData();
        }

        set((state) => {
            const article = state.articles[articleId];
            if (!article) {
                return state;
            }

            const clonedInstance = cloneArticleInstance(articleInstance);
            const selectedPoiIndex = state.poiSelection.current.index;
            const selectedPoiGuid =
                selectedPoiIndex !== undefined ? state.pois[selectedPoiIndex]?.poi.poiGuid : undefined;
            const preserveManualConnectorRelations =
                isNewPlacement &&
                state.manualPlacementData.articleId === articleId &&
                (state.manualPlacementData.settingType === SettingModelSettingType.Connectors ||
                    state.manualPlacementData.settingType === SettingModelSettingType.Anchors);

            // Preserve solver-provided corner and wall relations; use the selected POI only as a fallback.
            if ((!preserveManualConnectorRelations || !clonedInstance.poiGuids?.length) && selectedPoiGuid) {
                clonedInstance.poiGuids = [selectedPoiGuid];
            }

            if (Object.keys(clonedInstance.parts).length > 0) {
                initialParts.delete(articleInstanceId);
            }

            removeCachedInstanceEdges([articleInstanceId]);

            const newOrUpdatedArticle = {
                ...article,
                articleInstances: { ...article.articleInstances, [articleInstanceId]: clonedInstance }
            };

            if (propagateUpdate) {
                if (selectedPoiIndex !== undefined) {
                    const poi = state.pois[selectedPoiIndex].poi;
                    scheduleBatchPlace(
                        [
                            {
                                articleGuid: articleId,
                                articleNumber: state.articles[articleId].articleNumber,
                                lifeCycleCode: state.articles[articleId].lifeCycleCode,
                                representationGuid: EMPTY_GUID,
                                instances: [
                                    mapArticleInstanceToPlacement(
                                        articleInstanceId,
                                        clonedInstance,
                                        clonedInstance.poiGuids?.length ? clonedInstance.poiGuids : [poi.poiGuid],
                                        isNewPlacement
                                    )
                                ]
                            }
                        ],
                        get
                    );
                }
            }

            return { articles: { ...state.articles, [articleId]: newOrUpdatedArticle } };
        });
    },
    syncArticleInstanceParts: (articleId, partNames) => {
        if (!partNames || partNames.length === 0) {
            return;
        }

        const uniquePartNames = [...new Set(partNames.filter((name) => name && name.trim() !== ''))];
        if (uniquePartNames.length === 0) {
            return;
        }

        set((state) => {
            const article = state.articles[articleId];
            if (!article) {
                return state;
            }

            let hasChanges = false;
            let updatedInstances = article.articleInstances;

            Object.entries(article.articleInstances).forEach(([instanceId, instance]) => {
                const missingPartNames = uniquePartNames.filter((name) => !(name in instance.parts));
                if (missingPartNames.length === 0) {
                    return;
                }

                const initialPart = getAnchorPart(instance) ?? initialParts.get(instanceId);

                const clonedInstance = cloneArticleInstance(instance);
                missingPartNames.forEach((partName) => {
                    clonedInstance.parts[partName] = clonePart(initialPart);
                });

                if (!hasChanges) {
                    updatedInstances = { ...article.articleInstances };
                    hasChanges = true;
                }

                updatedInstances[instanceId] = clonedInstance;
                initialParts.delete(instanceId);
            });

            if (!hasChanges) {
                // only update partNames of the article if no instances were changed
                return {
                    articles: {
                        ...state.articles,
                        [articleId]: {
                            ...article,
                            partNames: uniquePartNames
                        }
                    }
                };
            }

            return {
                articles: {
                    ...state.articles,
                    [articleId]: {
                        ...article,
                        partNames: uniquePartNames,
                        articleInstances: updatedInstances
                    }
                }
            };
        });
    },
    updateArticleInstance: (articleId, articleInstanceId, articleInstances) => {
        if (!articleInstances[articleInstanceId]) {
            return;
        }

        const state = get();
        const updatedArticleInstance = cloneArticleInstance(articleInstances[articleInstanceId]);

        state.addOrUpdateArticleInstance(articleId, articleInstanceId, updatedArticleInstance, true, false);
    },

    updateArticleInstances: (updatedInstances, isNewPlacement) => {
        if (!updatedInstances || updatedInstances.length === 0) {
            return;
        }

        const state = get();

        const selectedPoiIndex = state.poiSelection.current.index;
        const selectedPoiGuid = selectedPoiIndex !== undefined ? state.pois[selectedPoiIndex]?.poi.poiGuid : undefined;

        const resolvedPoiGuids = (poiGuids: string[] | undefined) => {
            const validPoiGuids = (poiGuids ?? []).filter((poiGuid) => poiGuid && poiGuid !== EMPTY_GUID);
            return validPoiGuids.length > 0 ? validPoiGuids : selectedPoiGuid ? [selectedPoiGuid] : [];
        };

        if (isNewPlacement && state.arePivotControlsActive) {
            state.updatePivotControls();
        }

        removeCachedInstanceEdges(updatedInstances.map(({ instanceId }) => instanceId));

        const groupedByArticleAndPoi = new Map<
            string,
            Map<string, Array<{ instanceId: string; instance: ArticleInstance }>>
        >();

        startBatchProcessing();
        set(() => {
            const state = get();
            const updatedArticles = { ...state.articles };

            const poiGuidToIndex = new Map<string, number>();
            const instanceToPoiIndices = new Map<string, Set<number>>();
            state.pois.forEach((poiData, index) => {
                poiGuidToIndex.set(poiData.poi.poiGuid, index);
                Object.values(poiData.articles).forEach((instanceIds) => {
                    instanceIds.forEach((instanceId) => {
                        const poiIndices = instanceToPoiIndices.get(instanceId) ?? new Set<number>();
                        poiIndices.add(index);
                        instanceToPoiIndices.set(instanceId, poiIndices);
                    });
                });
            });

            const articleInstancesByArticleId = new Map<string, Record<string, ArticleInstance>>();
            let updatedPois = state.pois;
            let hasPoiChanges = false;

            const getPoiIndex = (poiGuid: string | null | undefined): number | undefined => {
                if (!poiGuid || poiGuid === EMPTY_GUID) {
                    return undefined;
                }
                return poiGuidToIndex.get(poiGuid);
            };

            const ensureUpdatedPois = () => {
                if (!hasPoiChanges) {
                    updatedPois = [...state.pois];
                    hasPoiChanges = true;
                }
            };

            const removeInstanceFromPoi = (poiIndex: number, instanceId: string) => {
                const existingPoi = updatedPois[poiIndex];
                if (!existingPoi) {
                    return;
                }

                const updatedArticleMap = { ...existingPoi.articles };
                Object.entries(updatedArticleMap).forEach(([articleGuid, instanceIds]) => {
                    const filtered = instanceIds.filter((id) => id !== instanceId);
                    if (filtered.length > 0) {
                        updatedArticleMap[articleGuid] = filtered;
                    } else {
                        delete updatedArticleMap[articleGuid];
                    }
                });

                updatedPois[poiIndex] =
                    existingPoi.autoFormworkPlaced && Object.keys(updatedArticleMap).length === 0
                        ? { ...existingPoi, articles: updatedArticleMap, autoFormworkPlaced: false }
                        : { ...existingPoi, articles: updatedArticleMap };
            };

            const addInstanceToPoi = (poiIndex: number, articleId: string, instanceId: string) => {
                const existingPoi = updatedPois[poiIndex];
                if (!existingPoi) {
                    return;
                }

                const existingIds = existingPoi.articles[articleId] ?? [];
                if (existingIds.includes(instanceId)) {
                    return;
                }

                updatedPois[poiIndex] = {
                    ...existingPoi,
                    articles: {
                        ...existingPoi.articles,
                        [articleId]: [...existingIds, instanceId]
                    }
                };
            };

            for (const { articleId, instanceId, instance } of updatedInstances) {
                const article = updatedArticles[articleId];
                if (!article) {
                    continue;
                }

                const clonedInstance = cloneArticleInstance(instance);
                clonedInstance.poiGuids = resolvedPoiGuids(clonedInstance.poiGuids);

                if (!articleInstancesByArticleId.has(articleId)) {
                    const articleInstances = { ...article.articleInstances };
                    articleInstancesByArticleId.set(articleId, articleInstances);
                    updatedArticles[articleId] = { ...article, articleInstances };
                }
                articleInstancesByArticleId.get(articleId)![instanceId] = clonedInstance;

                const relatedPoiIndices = new Set<number>();
                clonedInstance.poiGuids?.forEach((poiGuid) => {
                    const targetPoiIndex = getPoiIndex(poiGuid);
                    if (targetPoiIndex !== undefined) {
                        getRelatedPoiIndices(state.pois, targetPoiIndex).forEach((poiIndex) =>
                            relatedPoiIndices.add(poiIndex)
                        );
                    }
                });
                const previousPoiIndices = instanceToPoiIndices.get(instanceId) ?? new Set<number>();

                if (
                    previousPoiIndices.size !== relatedPoiIndices.size ||
                    [...previousPoiIndices].some((poiIndex) => !relatedPoiIndices.has(poiIndex))
                ) {
                    ensureUpdatedPois();

                    previousPoiIndices.forEach((poiIndex) => {
                        removeInstanceFromPoi(poiIndex, instanceId);
                    });

                    relatedPoiIndices.forEach((poiIndex) => {
                        addInstanceToPoi(poiIndex, articleId, instanceId);
                    });

                    instanceToPoiIndices.set(instanceId, relatedPoiIndices);
                }

                const poiGuidsForPlacement = clonedInstance.poiGuids ?? [];

                if (!groupedByArticleAndPoi.has(articleId)) {
                    groupedByArticleAndPoi.set(articleId, new Map());
                }
                const groupedByPoi = groupedByArticleAndPoi.get(articleId)!;
                const placementKey = poiGuidsForPlacement.join('|');
                if (!groupedByPoi.has(placementKey)) {
                    groupedByPoi.set(placementKey, []);
                }
                groupedByPoi.get(placementKey)!.push({ instanceId, instance: clonedInstance });
            }

            return hasPoiChanges ? { articles: updatedArticles, pois: updatedPois } : { articles: updatedArticles };
        });

        finishBatchProcessing();

        requestAnimationFrame(() => {
            get().updateArticleEdgesOverlay();
        });

        const placements: ArticleGroupPlacement[] = [];
        groupedByArticleAndPoi.forEach((groupedByPoi, articleId) => {
            const article = state.articles[articleId];
            if (!article) {
                return;
            }

            groupedByPoi.forEach((instances) => {
                placements.push({
                    articleGuid: articleId,
                    articleNumber: article.articleNumber,
                    lifeCycleCode: article.lifeCycleCode,
                    representationGuid: EMPTY_GUID,
                    instances: instances.map(({ instanceId, instance }) =>
                        mapArticleInstanceToPlacement(instanceId, instance, instance.poiGuids ?? [], false)
                    )
                });
            });
        });
        if (placements.length > 0) {
            scheduleBatchPlace(placements, get);
        }
    },

    replaceArticleInstance: (sourceArticleId, targetArticleId, sourceInstanceId, parts) => {
        const state = get();
        const sourceInstance = state.articles[sourceArticleId]?.articleInstances[sourceInstanceId];
        const targetArticle = state.articles[targetArticleId];
        if (!sourceInstance || !targetArticle || parts.length === 0) {
            return undefined;
        }

        const linkedPoiGuids = [
            ...new Set(
                sourceInstance.poiGuids?.length
                    ? sourceInstance.poiGuids
                    : state.pois
                          .filter((poiData) =>
                              Object.values(poiData.articles).some((instanceIds) =>
                                  instanceIds.includes(sourceInstanceId)
                              )
                          )
                          .map((poiData) => poiData.poi.poiGuid)
            )
        ].filter((poiGuid) => poiGuid && poiGuid !== EMPTY_GUID);
        const replacementId = uuidv4();
        const replacement: ArticleInstance = {
            ...cloneArticleInstance(sourceInstance),
            parts: mapPartsArrayToRecord(parts),
            poiGuids: linkedPoiGuids
        };

        removeCachedInstanceEdges([replacementId]);
        set((currentState) => {
            const article = currentState.articles[targetArticleId];
            if (!article) {
                return currentState;
            }

            return {
                articles: {
                    ...currentState.articles,
                    [targetArticleId]: {
                        ...article,
                        articleInstances: { ...article.articleInstances, [replacementId]: replacement }
                    }
                }
            };
        });

        linkedPoiGuids.forEach((poiGuid) => {
            const poiIndex = state.pois.findIndex((poiData) => poiData.poi.poiGuid === poiGuid);
            if (poiIndex >= 0) {
                getRelatedPoiIndices(state.pois, poiIndex).forEach((relatedPoiIndex) =>
                    state.addArticleToPoi(relatedPoiIndex, targetArticleId, replacementId)
                );
            }
        });

        const currentState = get();
        const targetInstance = currentState.articles[targetArticleId]?.articleInstances[replacementId];
        if (!targetInstance) {
            return undefined;
        }
        currentState.replaceInstanceInGroups(sourceInstanceId, targetArticleId, replacementId);
        scheduleBatchPlace(
            [
                {
                    articleGuid: targetArticleId,
                    articleNumber: targetArticle.articleNumber,
                    lifeCycleCode: targetArticle.lifeCycleCode,
                    representationGuid: EMPTY_GUID,
                    instances: [mapArticleInstanceToPlacement(replacementId, targetInstance, linkedPoiGuids, true)]
                }
            ],
            get
        );

        currentState.removeArticleInstances({ [sourceArticleId]: [sourceInstanceId] }, true);
        currentState.setArticleInstanceSelections({ [replacementId]: undefined });
        return replacementId;
    },

    addArticleInstance: (articleId, parts, isDragNewArticle) => {
        const state = get();
        const selectedPoiIndex = state.poiSelection.current.index;

        if (!articleId || !parts || parts.length === 0 || selectedPoiIndex === undefined) {
            return undefined;
        }

        if (isDragNewArticle) {
            state.beginHistoryTransaction('historyPlace');
        }
        const articleInstanceId = uuidv4();
        if (isDragNewArticle) {
            state.setArticleInstanceSelections({ [articleInstanceId]: undefined });
            state.setIsDragNewArticleStartRef(false);
            state.setIsDragNewArticleActiveRef(true);

            const mainWebGLRendererRef = state.mainWebGLRendererRef;
            if (mainWebGLRendererRef.current) {
                mainWebGLRendererRef.current.domElement.style.cursor = 'wait';
            }
        }

        const selectedPoi = state.pois[selectedPoiIndex]?.poi;
        const relatedCornerPoiGuids =
            state.manualPlacementData.articleId === articleId &&
            state.manualPlacementData.settingType === SettingModelSettingType.Corners &&
            selectedPoi &&
            isCornerSingleSidePoi(selectedPoi)
                ? [selectedPoi.poiGuid, selectedPoi.faceA.poiGuid, selectedPoi.faceB.poiGuid]
                : [];
        const relatedPoiGuids =
            state.manualPlacementData.articleId === articleId &&
            (state.manualPlacementData.settingType === SettingModelSettingType.Connectors ||
                state.manualPlacementData.settingType === SettingModelSettingType.Anchors)
                ? state.manualPlacementData.relatedPoiGuids
                : relatedCornerPoiGuids;
        const newArticleInstance: ArticleInstance = {
            parts: mapPartsArrayToRecord(parts),
            ...(relatedPoiGuids.length > 0 ? { poiGuids: [...relatedPoiGuids] } : {})
        };

        const partKeys = state.articles[articleId]?.partNames;
        if (!partKeys || partKeys.length === 0) {
            initialParts.set(articleInstanceId, newArticleInstance.parts[Object.keys(newArticleInstance.parts)[0]]);
        }

        state.addOrUpdateArticleInstance(articleId, articleInstanceId, newArticleInstance, !isDragNewArticle, true);

        const poiGuids = newArticleInstance.poiGuids?.length
            ? newArticleInstance.poiGuids
            : [state.pois[selectedPoiIndex].poi.poiGuid];
        const relatedPoiIndices = new Set<number>();
        poiGuids.forEach((poiGuid) => {
            const poiIndex = state.pois.findIndex((poiData) => poiData.poi.poiGuid === poiGuid);
            if (poiIndex >= 0) {
                getRelatedPoiIndices(state.pois, poiIndex).forEach((relatedPoiIndex) =>
                    relatedPoiIndices.add(relatedPoiIndex)
                );
            }
        });
        relatedPoiIndices.forEach((relatedPoiIndex) =>
            state.addArticleToPoi(relatedPoiIndex, articleId, articleInstanceId)
        );

        // Calculate z position of the currently selected wall's bottom edge
        if (articleInstanceId && isTopBottomCameraView(raycastPlane.normal) && state.faceSelectionRef.current) {
            const poiIndex = state.poiSelection.current.index;
            if (poiIndex !== undefined) {
                const poi = state.pois[poiIndex]?.poi;
                if (poi && isPlanarFacePoi(poi)) {
                    const edges = poi.formFace.outerWire.edges;
                    const bottomZ = Math.min(...edges.map((e) => e.startPoint.z));
                    state.setWallBottomZ(bottomZ);
                }
            }
        }
    },
    removeAllArticleInstances: (articleId, propagateUpdate) => {
        const instanceIds = Object.keys(get().articles[articleId]?.articleInstances ?? {});
        get().removeArticleInstances({ [articleId]: instanceIds }, propagateUpdate);
    },

    removeArticleInstances: (instancesByArticle, propagateUpdate) => {
        get().clearManualPlacementData();
        const state = get();
        const instancesToRemove = new Map(
            Object.entries(instancesByArticle).map(([articleId, instanceIds]) => [articleId, new Set(instanceIds)])
        );

        for (const [articleId, instanceIds] of Object.entries(instancesByArticle)) {
            const article = state.articles[articleId];
            if (!article || !isSupportingConstruction(article.articleNumber)) {
                continue;
            }

            for (const instanceId of instanceIds) {
                if (!(instanceId in article.articleInstances)) {
                    continue;
                }

                const group = state.getGroupByInstanceId(instanceId);
                const supportCount =
                    group?.members.filter((member) =>
                        isSupportingConstruction(state.articles[member.articleId]?.articleNumber ?? null)
                    ).length ?? 0;
                if (
                    supportCount !== 1 ||
                    !group?.members.some((member) => member.articleId === articleId && member.instanceId === instanceId)
                ) {
                    continue;
                }

                for (const member of group?.members ?? []) {
                    const memberArticle = state.articles[member.articleId];
                    if (
                        memberArticle &&
                        isFramiWedgeClamp(memberArticle.articleNumber) &&
                        member.instanceId in memberArticle.articleInstances
                    ) {
                        const relatedInstances = instancesToRemove.get(member.articleId) ?? new Set<string>();
                        relatedInstances.add(member.instanceId);
                        instancesToRemove.set(member.articleId, relatedInstances);
                    }
                }
            }
        }

        const expandedInstancesByArticle = Object.fromEntries(
            [...instancesToRemove].map(([articleId, instanceIds]) => [articleId, [...instanceIds]])
        );

        set((state) => {
            const allInstanceIds: string[] = [];
            const updatedArticles = { ...state.articles };
            const mutableInstancesByArticle: Record<string, string[]> = {};
            const instanceIds: Set<string> = new Set();

            Object.entries(expandedInstancesByArticle).forEach(([articleId, articleInstanceIds]) => {
                if (!articleInstanceIds || articleInstanceIds.length === 0) {
                    return;
                }

                for (const id of articleInstanceIds) {
                    instanceIds.add(id);
                    state.hiddenInstanceIds.delete(id);
                }

                const article = state.articles[articleId];
                if (!article) {
                    return;
                }
                mutableInstancesByArticle[articleId] = articleInstanceIds;
            });

            Object.entries(mutableInstancesByArticle).forEach(([articleId, articleInstanceIds]) => {
                if (!articleInstanceIds || articleInstanceIds.length === 0) {
                    return;
                }
                const article = state.articles[articleId];
                if (!article) {
                    return;
                }

                const remainingInstances = { ...article.articleInstances };
                articleInstanceIds.forEach((instanceId) => {
                    delete remainingInstances[instanceId];
                    allInstanceIds.push(instanceId);
                    initialParts.delete(instanceId);
                });

                updatedArticles[articleId] = { ...article, articleInstances: remainingInstances };
            });

            if (allInstanceIds.length === 0) {
                return state;
            }

            state.removeCollidersByRelations(allInstanceIds);
            removeCachedInstanceEdges(allInstanceIds);

            if (propagateUpdate) {
                scheduleBatchDelete(allInstanceIds, get);
            }

            return {
                articles: updatedArticles,
                ...(propagateUpdate
                    ? {
                          placementUpdate: { event: PlacementUpdateEvent.DELETE, articleInstanceIds: instanceIds }
                      }
                    : {})
            };
        });

        // Domain indexes must be updated before another placement can run.
        const removedIds = Object.values(expandedInstancesByArticle).flat();
        get().removeInstancesFromGroups(removedIds);
        get().removeArticlesFromPois(
            Object.entries(expandedInstancesByArticle).flatMap(([articleId, ids]) =>
                ids.map((articleInstanceId) => ({ articleId, articleInstanceId }))
            )
        );

        requestAnimationFrame(() => {
            get().updateArticleEdgesOverlay();
        });
    },

    insertArticleInstance: (cursorPosition) => {
        const state = get();
        if (!state.selectedArticleId) {
            return;
        }

        // prevent duplicate insertion if a drag-insert is already active
        if (state.isDragNewArticleActiveRef.current) {
            return;
        }

        // Set initial position
        if (cursorPosition) {
            initialPosition.set(cursorPosition.x, cursorPosition.y, cursorPosition.z);
        } else if (state.pointerPositionRef.current) {
            initialPosition.set(
                state.pointerPositionRef.current.x,
                state.pointerPositionRef.current.y,
                state.pointerPositionRef.current.z
            );
        } else if (state.faceSelectionRef.current) {
            const positionAttribute = state.faceSelectionRef.current.geometry.attributes.position;
            initialPosition.fromBufferAttribute(positionAttribute, 0);
        } else {
            initialPosition.set(0, 0, 0);
        }

        const useManualConnectorPlane =
            state.manualPlacementData.articleId === state.selectedArticleId &&
            state.manualPlacementData.candidates.length > 0;
        const isCornerPoi = state.poiSelection.current.filterType === POIFilterType.Corner;
        const isManualConnector =
            useManualConnectorPlane && state.manualPlacementData.settingType === SettingModelSettingType.Connectors;
        const isManualCorner =
            useManualConnectorPlane && state.manualPlacementData.settingType === SettingModelSettingType.Corners;
        const isManualAnchor =
            useManualConnectorPlane && state.manualPlacementData.settingType === SettingModelSettingType.Anchors;
        const nearestCornerCandidate =
            (isManualConnector || isManualCorner) && isCornerPoi
                ? getNearestSnapCandidate(state.manualPlacementData.candidates, initialPosition)
                : undefined;

        if (useManualConnectorPlane && state.manualPlacementData.plane && !nearestCornerCandidate) {
            state.manualPlacementData.plane.projectPoint(initialPosition, initialPosition);
        }
        // Project initial position onto the workingplane
        else if (state.workingPlane) {
            state.workingPlane.projectPoint(initialPosition, initialPosition);
        }

        const nearestManualTarget =
            isManualAnchor || (isManualConnector && !isCornerPoi)
                ? findNearestConnectorSnapTarget(
                      initialPosition,
                      state.mainRaycasterRef.current?.ray ?? new Ray(),
                      state.manualPlacementData.snapPoints,
                      state.manualPlacementData.snapRanges,
                      Number.POSITIVE_INFINITY,
                      false
                  )
                : null;

        if (nearestManualTarget) {
            initialPosition.set(
                nearestManualTarget.placement.position.x,
                nearestManualTarget.placement.position.y,
                nearestManualTarget.placement.position.z
            );
        }

        const alignment = nearestCornerCandidate
            ? new Vector3(
                  nearestCornerCandidate.alignment.x,
                  nearestCornerCandidate.alignment.y,
                  nearestCornerCandidate.alignment.z
              )
            : nearestManualTarget
              ? new Vector3(
                    nearestManualTarget.placement.alignment.x,
                    nearestManualTarget.placement.alignment.y,
                    nearestManualTarget.placement.alignment.z
                )
              : useManualConnectorPlane && state.manualPlacementData.plane
                ? state.manualPlacementData.plane.normal.clone()
                : (state.workingPlane?.normal?.clone() ?? raycastPlane.normal.clone());

        if (nearestCornerCandidate) {
            initialPosition.set(
                nearestCornerCandidate.position.x,
                nearestCornerCandidate.position.y,
                nearestCornerCandidate.position.z
            );
        }

        const isTopBottomView = isTopBottomCameraView(raycastPlane.normal);
        const normal = isTopBottomView ? raycastPlane.normal : alignment;
        raycastPlane.setFromNormalAndCoplanarPoint(normal, initialPosition);

        let initialRotation = 0;

        if (nearestCornerCandidate) {
            initialRotation = nearestCornerCandidate.rotation;
        }

        if (useManualConnectorPlane && state.manualPlacementData.candidates.length > 0 && !isCornerPoi) {
            const nearestCandidate = getNearestSnapCandidate(state.manualPlacementData.candidates, initialPosition);
            initialRotation = nearestCandidate?.rotation ?? 0;
        }

        if (nearestManualTarget) {
            initialRotation = nearestManualTarget.placement.rotation;
        }

        const selectedArticleNumber = state.articles[state.selectedArticleId]?.articleNumber ?? null;
        const adjustableMainPartName = isAdjustableFramiClamp(selectedArticleNumber)
            ? getConnectorMainPartName(state.selectedArticleId, state.articleDragRefsMap.current)
            : undefined;
        const adjustableMainSnapPoint = adjustableMainPartName
            ? state.manualPlacementData.snapPoints
                  .filter((point) => point.placement.name === adjustableMainPartName && point.pairedPlacement)
                  .map((point) => ({ point, candidate: point.placement }))
                  .reduce<{
                      point: (typeof state.manualPlacementData.snapPoints)[number];
                      candidate: PartPlacement;
                  } | null>((nearest, current) => {
                      const distance = new Vector3(
                          current.candidate.position.x,
                          current.candidate.position.y,
                          current.candidate.position.z
                      ).distanceTo(initialPosition);
                      const nearestDistance = nearest
                          ? new Vector3(
                                nearest.candidate.position.x,
                                nearest.candidate.position.y,
                                nearest.candidate.position.z
                            ).distanceTo(initialPosition)
                          : Number.POSITIVE_INFINITY;
                      return distance < nearestDistance ? current : nearest;
                  }, null)?.point
            : undefined;

        if (adjustableMainSnapPoint?.pairedPlacement) {
            initialPosition.set(
                adjustableMainSnapPoint.placement.position.x,
                adjustableMainSnapPoint.placement.position.y,
                adjustableMainSnapPoint.placement.position.z
            );
            alignment.set(
                adjustableMainSnapPoint.placement.alignment.x,
                adjustableMainSnapPoint.placement.alignment.y,
                adjustableMainSnapPoint.placement.alignment.z
            );
            initialRotation = adjustableMainSnapPoint.placement.rotation;
        }

        const parts: PartPlacement[] = [];
        if (nearestManualTarget?.pairedPlacement) {
            parts.push(nearestManualTarget.placement, nearestManualTarget.pairedPlacement);
        } else if (isManualConnector && nearestCornerCandidate) {
            parts.push(nearestCornerCandidate);
        } else if (adjustableMainSnapPoint?.pairedPlacement) {
            parts.push(adjustableMainSnapPoint.placement, adjustableMainSnapPoint.pairedPlacement);
        } else if (isManualCorner) {
            parts.push(...state.manualPlacementData.candidates);
        } else {
            const partKeys = state.articles[state.selectedArticleId]?.partNames;
            if (partKeys && partKeys.length > 0) {
                for (const partName of partKeys) {
                    const partPlacement: PartPlacement = {
                        name: partName,
                        position: initialPosition,
                        alignment,
                        rotation: initialRotation,
                        scale: { x: 1, y: 1, z: 1 }
                    };
                    parts.push(partPlacement);
                }
            }
        }

        if (useManualConnectorPlane && state.manualPlacementData.plane) {
            state.setIsZAxisDragActiveRef(isManualCorner);
        }

        state.addArticleInstance(state.selectedArticleId, parts, true);
    },

    hiddenInstanceIds: new Set<string>(),
    resetHiddenInstanceIds: () => {
        const state = get();
        if (state.hiddenInstanceIds.size > 0) {
            state.updateInstanceVisibility([...state.hiddenInstanceIds], true);
        }
        set({ hiddenInstanceIds: new Set<string>() });
    },

    articleInstanceSelections: {},
    isPanelQuickReplacementOpen: false,
    isPanelQuickReplacementGridActive: false,
    setIsPanelQuickReplacementGridActive: (isActive) => set({ isPanelQuickReplacementGridActive: isActive }),
    setIsPanelQuickReplacementOpen: (isOpen) => {
        if (isOpen && get().isViewMode) {
            return;
        }
        if (isOpen && (get().isTransformationActiveRef.current || get().articleTransformationMode !== TransformationMode.NONE)) {
            get().cancelArticleTransformation();
        }
        if (isOpen) {
            get().clearHoveredArticleHighlight();
        }
        set({
            isPanelQuickReplacementOpen: isOpen,
            isPanelQuickReplacementGridActive: false,
            ...(isOpen ? { arePivotControlsActive: false } : {})
        });
    },
    setArticleInstanceSelections: (selections) =>
        set(() => {
            const state = get();
            const selectedInstanceIds = Object.keys(selections);
            const isDragRefRegistered =
                state.articleDragRefsMap.current.get(state.selectedArticleId ?? '') !== undefined;

            // Fallback: update z position when dragging got interrupted during top/bottom view insertion
            if (state.isZPositionUpdatePending && selectedInstanceIds.length === 1 && isDragRefRegistered) {
                placeInstanceOnWall(
                    selectedInstanceIds[0],
                    state.selectedArticleId,
                    state,
                    state.articleDragRefsMap.current
                );
                state.setIsZPositionUpdatePending(false);
            }

            return {
                articleInstanceSelections: selections
            };
        }),
    selectAllArticleInstances: () =>
        set((state) => {
            const selections: Record<string, number | undefined> = {};
            for (const article of Object.values(state.articles)) {
                for (const [instanceId] of Object.entries(article.articleInstances)) {
                    selections[instanceId] = undefined;
                }
            }
            return { articleInstanceSelections: selections };
        }),
    addArticleInstanceSelection: (articleInstanceId, index) =>
        set((state) => {
            return {
                articleInstanceSelections: {
                    ...state.articleInstanceSelections,
                    [articleInstanceId]: index
                }
            };
        }),
    removeArticleInstanceSelection: (articleInstanceId) =>
        set((state) => {
            const { [articleInstanceId]: _, ...rest } = state.articleInstanceSelections;
            return {
                articleInstanceSelections: rest
            };
        }),
    updateArticleInstanceSelection: (articleInstanceId, index) =>
        set((state) => {
            state.articleInstanceSelections[articleInstanceId] = index;
            return state;
        }),

    lastSelectionCache: {},
    setLastSelectionCache: (selections) => set({ lastSelectionCache: selections }),

    discardArticleInstanceSelection: (deleteArticles) => {
        const state = get();
        if (deleteArticles && state.isViewMode) {
            return;
        }

        const isAutoDragActive = state.isAutoDragActiveRef.current;
        const arePivotControlsActive = state.arePivotControlsActive;

        const isInsertionActive =
            state.isDragNewArticleStartRef.current || state.isDragNewArticleActiveRef.current || isAutoDragActive;

        const isDeleteActive = deleteArticles || isInsertionActive;

        if (
            !isDeleteActive &&
            ((arePivotControlsActive && state.articleTransformationMode !== TransformationMode.NONE) ||
                state.isDraggingActive())
        ) {
            state.cancelArticleTransformation();
            return;
        }

        const selectedArticleInstanceIds = Object.keys(state.articleInstanceSelections);
        if (selectedArticleInstanceIds.length === 0) {
            return;
        }

        if (arePivotControlsActive) {
            state.updatePivotControls();
        }

        if (isDeleteActive) {
            const instancesGroupedByArticle: Record<string, string[]> = {};
            const selectedSet = new Set(selectedArticleInstanceIds);
            const propagateUpdate = !state.isAutoDragActiveRef.current;

            Object.entries(state.articles).forEach(([articleId, article]) => {
                const matched = Object.keys(article.articleInstances).filter((instanceId) =>
                    selectedSet.has(instanceId)
                );
                if (matched.length > 0) {
                    instancesGroupedByArticle[articleId] = matched;
                }
            });

            state.removeArticleInstances(instancesGroupedByArticle, propagateUpdate);

            if (isInsertionActive || state.isDraggingActive()) {
                state.handleOnDragArticleEnd(true);
            }
        }

        state.resetHiddenPOIFilterTypes();

        if (isAutoDragActive || arePivotControlsActive) {
            state.setArticleInstanceSelections(state.lastSelectionCache);
        } else {
            state.setArticleInstanceSelections({});
            state.setLastSelectionCache({});
        }

        invalidate();
    }
});
